import {DialogTrigger} from 'react-aria-components'

import CategoryIcon from '@/assets/food/icons/category.svg?react'
import CategoryDefault from '@/assets/food/icons/categoryDefault.svg?react'
import PlusIcon from '@/assets/food/icons/plus.svg?react'
import StoreDefault from '@/assets/food/icons/storeDefault.svg?react'
import ArrowLeftIcon from '@/assets/homepage/icon/arrow-left.svg?react'
import ArrowRightIcon from '@/assets/homepage/icon/arrow-right.svg?react'
import CloseIcon from '@/assets/icons/close.svg?react'
import SearchIcon from '@/assets/icons/search.svg?react'
import MenuItemPlaceHolder from '@/components/MenuItem/MenuItemPlaceHolder'
import type {EnhancedFeed, RestaurantDetail} from '@/lib/food-grubhub/foodRestaurantDetailModel'
import useGrubhubGetAuth from '@/lib/food-grubhub/hooks/useGrubhubGetAuth'
import useMenuItemListing from '@/lib/food-grubhub/hooks/useMenuItemListing'
import {useGrubhubUserAddress} from '@/lib/food-grubhub/store'
import {
  FoodGrubhubDetailRoute,
  //  FoodGrubhubMenuItemDetailRoute
} from '@/routeRegistry'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import {useIsMobile} from '@/utils/hooks/useWindowSize'
import useSetSearch from '@/utils/router/useSetSearch'
import CategoryItemPrice from '@/views/FoodGrubhubRestaurantDetail/components/CategoryItemPrice'
import {MenuItemDetailDialog} from '@/views/FoodGrubhubRestaurantDetail/components/MenuItemDetailDialog/MenuItemDetailDialog'
import ReadMoreString from '@/views/FoodGrubhubRestaurantDetail/components/ReadMoreString'
import style from '@/views/FoodGrubhubRestaurantDetail/FoodGrubhubRestaurantDetail.module.scss'

const ImageDefaultLoader = ({value}: {value: 'store' | 'category'}) => {
  return (
    <div className={style.StoreImageDefaultContainer}>
      {value === 'store' ? <StoreDefault /> : <CategoryDefault />}
    </div>
  )
}

interface MenuItemPlaceHolder {
  maxNumberOfItems: number
}

const MenuItemListPagePlaceholder = memo<MenuItemPlaceHolder>(function MenuItemListPagePlaceholder({
  maxNumberOfItems,
}) {
  const dummy = new Array(maxNumberOfItems).fill(undefined)

  return (
    <div className={style.MenuItemListLoading}>
      {dummy.map((_, idx) => (
        <div key={idx.toString()}>
          <MenuItemPlaceHolder />
        </div>
      ))}
    </div>
  )
})

type DialogState = {type: 'closed'} | {type: 'customize'}

interface Props {
  menuItemCategoryList: EnhancedFeed[]
  restaurantDetail?: RestaurantDetail
  restaurantId: string
  operationId: string
}

export default function FoodGrubhubMenuItemListingV2({
  menuItemCategoryList,
  restaurantDetail,
  restaurantId,
  operationId,
}: Props) {
  const {menuItemId} = FoodGrubhubDetailRoute.useSearch()
  const [selectedMenuItemCategoryId, setSelectedMenuItemCategoryId] = useState('')
  const [activeIndex, setActiveIndex] = useState('')
  const [filterInput, setFilterInput] = useState('')
  const router = useRouter()
  const timestampRef = useRef(Date.now())
  const [storageUserLocation] = useGrubhubUserAddress()
  const [isMenuItemLoading, setIsMenuItemLoading] = useState(false)
  const [dialogState, setDialogState] = useState<DialogState>({type: 'closed'})
  const [selectedMenuItemId, setSelectedMenuItemId] = useState(menuItemId)
  const setMenuItemId = useSetSearch(FoodGrubhubDetailRoute.id, 'menuItemId')

  const isMobile = useIsMobile()

  const {data: authData} = useGrubhubGetAuth()

  const menuItemListingQuery = useMemo(
    () => ({
      time: timestampRef.current,
      location: `POINT(${storageUserLocation.longitude} ${storageUserLocation.latitude})`,
      operationId,
      isFutureOrder: false,
      restaurantStatus: 'ORDERABLE',
      brandUuid: '2d7a7540-f419-11e8-9737-5bbb692281b0',
      isNonRestaurantMerchant: false,
      merchantTypes: '',
      orderType: 'STANDARD',
      agent: false,
      weightedItemDataIncluded: true,
      task: 'CATEGORY',
      rpcVariant: 'PAGINATION_SUPPORTED',
      platform: 'WEB',
      restaurantId,
      menuItemId: selectedMenuItemCategoryId,
    }),
    [
      storageUserLocation.longitude,
      storageUserLocation.latitude,
      restaurantId,
      selectedMenuItemCategoryId,
      operationId,
    ],
  )

  const {data: menuItemListingData, isLoading: isMenuItemListingLoading} = useMenuItemListing(
    menuItemListingQuery,
    authData?.session_handle.access_token ?? '',
  )

  useEffect(() => {
    if (menuItemListingData) {
      setIsMenuItemLoading(false)
    }
  }, [menuItemListingData])

  useEffect(() => {
    setActiveIndex(menuItemCategoryList[0]?.id ?? '')
    setSelectedMenuItemCategoryId(menuItemCategoryList[0]?.id ?? '')
  }, [menuItemCategoryList])

  useEffect(() => {
    if (isMenuItemListingLoading) {
      setIsMenuItemLoading(true)
    } else {
      const timeout = setTimeout(() => {
        setIsMenuItemLoading(false)
      }, 300)
      return () => {
        clearTimeout(timeout)
      }
    }
  }, [isMenuItemListingLoading])

  const restaurantAvaibility = restaurantDetail?.restaurant_availability

  const handleSearchMenuItem = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        router.navigate({
          to: FoodGrubhubDetailRoute.fullPath,
          params: {restaurantId},
          search: {q: filterInput},
        })
      }
    },
    [router, restaurantId, filterInput],
  )

  const handleSelectedCategory = (categoryId: string) => {
    setIsMenuItemLoading(true)
    setActiveIndex(categoryId)
    setSelectedMenuItemCategoryId(categoryId)
    if (!isMobile) {
      window.scrollTo({top: 0, behavior: 'smooth'})
    }
    document.getElementById(`menuItem-heading-${categoryId}`)?.focus()
  }

  const currentIndex = menuItemCategoryList.findIndex(
    item => item.id === selectedMenuItemCategoryId,
  )

  const handlePrevCategory = () => {
    setIsMenuItemLoading(true)
    if (currentIndex > 0) {
      const prevCategory = menuItemCategoryList[currentIndex - 1] ?? {id: ''}
      setActiveIndex(prevCategory.id)
      setSelectedMenuItemCategoryId(prevCategory.id)
      if (!isMobile) {
        window.scrollTo({top: 0, behavior: 'smooth'})
      }
      document.getElementById(`menuItem-heading-${prevCategory.id}`)?.focus()
    }
  }

  const prevCategory = currentIndex > 0 ? menuItemCategoryList[currentIndex - 1] : null

  const handleNextCategory = () => {
    setIsMenuItemLoading(true)
    if (currentIndex < menuItemCategoryList.length - 1) {
      const nextCategory = menuItemCategoryList[currentIndex + 1] ?? {id: ''}
      setActiveIndex(nextCategory.id)
      setSelectedMenuItemCategoryId(nextCategory.id)
      if (!isMobile) {
        window.scrollTo({top: 0, behavior: 'smooth'})
      }
      document.getElementById(`menuItem-heading-${nextCategory.id}`)?.focus()
    }
  }

  const nextCategory =
    currentIndex < menuItemCategoryList.length - 1 ? menuItemCategoryList[currentIndex + 1] : null

  const handleCustomizeMenuItem = useCallback(
    (menuItemId: string): void => {
      setDialogState({type: 'customize'})
      setSelectedMenuItemId(menuItemId)
      setMenuItemId(menuItemId)
    },
    [setMenuItemId],
  )

  const handleDialogClose = useCallback(() => {
    setDialogState({type: 'closed'})
  }, [])

  return (
    <section className={style.PageContent}>
      <div className={style.CategoryContainer}>
        <div className={style.CategoryOptionContainer}>
          <div className={style.CategoryOption}>
            <CategoryIcon className={style.Icon} />
            <h2 className={style.Heading}>Categories</h2>
          </div>
          <div className={style.CategoryList} role='list'>
            {menuItemCategoryList.map(menuItemCategory => (
              <button
                key={menuItemCategory.id}
                type='button'
                onClick={() => {
                  handleSelectedCategory(menuItemCategory.id)
                }}
                aria-label={`Go to ${menuItemCategory.name} section`}
                className={clsx(
                  style.Item,
                  activeIndex === menuItemCategory.id && style.ItemActive,
                )}
              >
                <VisuallyHidden role='heading' aria-level={3}>
                  {menuItemCategory.name}
                </VisuallyHidden>
                {menuItemCategory.name}
              </button>
            ))}
          </div>
        </div>
        <div className={style.CategoryDetailContainer}>
          <div className={style.CategoryDetailFilter}>
            <div className={style.FilterContainer}>
              <div className={style.FilterSearchContainer}>
                <SearchIcon className={style.Icon} />
                <input
                  type='text'
                  placeholder='Filter result'
                  aria-label='Filter result'
                  value={filterInput}
                  onChange={e => {
                    setFilterInput(e.target.value)
                  }}
                  onKeyDown={e => {
                    handleSearchMenuItem(e)
                  }}
                  className={style.SearchBox}
                />
                {filterInput.length > 0 && (
                  <button
                    type='button'
                    aria-label='Clear search'
                    onClick={() => {
                      setFilterInput('')
                    }}
                    className={style.SearchBoxClearButtonContainer}
                  >
                    <CloseIcon className={style.SearchBoxClearButtonIcon} aria-hidden='true' />
                  </button>
                )}
              </div>
              <div className={style.FilterButtonContainer}>
                <div
                  className={clsx(style.FilterButton)}
                  aria-labelledby='delivery-time-label delivery-time-value'
                >
                  <p className={style.ButtonTitle}>Delivery time</p>
                  <div className={style.ButtonValue}>
                    {restaurantAvaibility?.delivery_estimate} mins
                  </div>
                </div>
              </div>
            </div>
            <div className={style.CategoryOptionMobile}>
              <div className={style.CategoryList} role='list' aria-label='List of menu items'>
                {menuItemCategoryList.map(menuItemCategory => (
                  <button
                    key={menuItemCategory.id}
                    type='button'
                    onClick={() => {
                      handleSelectedCategory(menuItemCategory.id)
                    }}
                    aria-label={`Go to ${menuItemCategory.name} section`}
                    className={clsx(
                      style.Item,
                      activeIndex === menuItemCategory.id && style.ItemActive,
                    )}
                  >
                    <VisuallyHidden role='heading' aria-level={3}>
                      {menuItemCategory.name}
                    </VisuallyHidden>
                    {menuItemCategory.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {isMenuItemLoading ? (
            <MenuItemListPagePlaceholder maxNumberOfItems={8} />
          ) : (
            <div className={style.CategoryDetailContent}>
              <div className={style.CategoryItem}>
                <h3
                  id={`menuItem-heading-${selectedMenuItemCategoryId}`}
                  tabIndex={-1}
                  className={style.CategoryItemHeading}
                >
                  <VisuallyHidden>Category:</VisuallyHidden>
                  {menuItemCategoryList.find(item => item.id === selectedMenuItemCategoryId)
                    ?.name ?? 'Category title'}
                </h3>
                <div className={style.ItemList} role='list'>
                  {menuItemListingData?.object.data.content.map(menuItem => {
                    const dollarPrice = (menuItem.entity?.item_price?.delivery?.value ?? 0) / 100
                    const itemImagePathBuilder = `${menuItem.entity?.media_image?.base_url}${menuItem.entity?.media_image?.public_id}.${menuItem.entity?.media_image?.format}`

                    return (
                      <div role='listitem' key={menuItem.entity?.item_id} className={style.Item}>
                        <div className={style.ItemInfo}>
                          <div className={style.ItemInfoHeader}>
                            <h4 className={style.ItemTitle}>{menuItem.entity?.item_name}</h4>
                            <div className={style.ItemDesc}>
                              <VisuallyHidden>
                                Description:{' '}
                                {menuItem.entity?.item_description?.length === 0 && 'Not available'}
                              </VisuallyHidden>
                              <ReadMoreString
                                text={menuItem.entity?.item_description ?? ''}
                                maxChars={100}
                              />
                            </div>
                          </div>
                          <div className={style.ItemBasePrice}>
                            <CategoryItemPrice price={dollarPrice} />
                          </div>
                        </div>
                        <div className={style.ItemImageContainer}>
                          {menuItem.entity?.media_image ? (
                            <img
                              src={itemImagePathBuilder}
                              alt=''
                              loading='lazy'
                              className={style.Image}
                            />
                          ) : (
                            <ImageDefaultLoader value='category' />
                          )}
                          <div className={style.AddingButtonContainer}>
                            <button
                              type='button'
                              aria-label={`Customize the ${menuItem.entity?.item_name} before adding to the cart`}
                              className={style.AddingButton}
                              onClick={() => {
                                handleCustomizeMenuItem(menuItem.entity?.item_id ?? '')
                              }}
                            >
                              <PlusIcon className={style.AddingButtonIcon} />
                            </button>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>

                <div className={style.CategoryNavigation}>
                  <button
                    type='button'
                    onClick={handlePrevCategory}
                    aria-label={
                      prevCategory ? `Previous category: ${prevCategory.name}` : 'Previous category'
                    }
                    disabled={currentIndex === 0}
                    className={clsx(
                      style.NavButton,
                      style.Prev,
                      currentIndex === 0 && style.NavButtonDisabled,
                    )}
                  >
                    <ArrowLeftIcon className={style.ButtonIcon} aria-hidden='true' />
                    <div className={style.ButtonText}>
                      Prev {prevCategory ? `– ${prevCategory.name}` : ''}
                    </div>
                  </button>

                  <button
                    type='button'
                    aria-label={
                      nextCategory ? `Next category: ${nextCategory.name}` : 'Next category'
                    }
                    onClick={handleNextCategory}
                    disabled={currentIndex === menuItemCategoryList.length - 1}
                    className={clsx(
                      style.NavButton,
                      style.Next,
                      currentIndex === menuItemCategoryList.length - 1 && style.NavButtonDisabled,
                    )}
                  >
                    <div className={style.ButtonText}>
                      Next {nextCategory ? `– ${nextCategory.name}` : ''}
                    </div>
                    <ArrowRightIcon aria-hidden='true' className={style.ButtonIcon} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <DialogTrigger
        isOpen={menuItemId !== '' || dialogState.type !== 'closed'}
        onOpenChange={isOpen => {
          if (!isOpen) {
            setDialogState({type: 'closed'})
            setMenuItemId('')
          }
        }}
      >
        <MenuItemDetailDialog onClose={handleDialogClose} menuItemId={selectedMenuItemId} />
      </DialogTrigger>
    </section>
  )
}
